// src/components/MainContent.tsx
import React from 'react';
import { AlertCircle, TrendingUp, HelpCircle, ArrowRight, RefreshCw, List, FileText, Edit2 } from 'lucide-react';
import { IncidentResponse } from '../types/incident';
import IncidentAnalysisComponent from './IncidentAnalysis';
import InfoCard from './InfoCard';
import { useLanguage } from '../context/LanguageContext';

interface MainContentProps {
    incidentDescription: string;
    onDescriptionChange: (value: string) => void;
    onSubmit: () => void;
    priority?: 'low' | 'medium' | 'high' | 'critical';
    onPriorityChange?: (priority: 'low' | 'medium' | 'high' | 'critical') => void;
    isSubmitting?: boolean;
    submitError?: string | null;
    createdIncident?: IncidentResponse | null;
    onCreateAnother?: () => void;
    analysisLoading?: boolean;
    analysisError?: string | null;
    onViewAllIncidents?: () => void;
    onEditIncident?: () => void;
}

export default function MainContent({
                                        incidentDescription,
                                        onDescriptionChange,
                                        onSubmit,
                                        priority = 'medium',
                                        onPriorityChange,
                                        isSubmitting = false,
                                        submitError,
                                        createdIncident,
                                        onCreateAnother,
                                        analysisLoading = false,
                                        analysisError,
                                        onViewAllIncidents,
                                        onEditIncident
                                    }: MainContentProps) {
    const { t } = useLanguage();

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit();
    };

    const quickTips = [
        t('tips.0'),
        t('tips.1'),
        t('tips.2'),
        t('tips.3')
    ];

    const priorityLevels = [
        { level: t('priority.critical'), description: t('priorityLevels.critical'), color: 'bg-red-500', value: 'critical' },
        { level: t('priority.high'), description: t('priorityLevels.high'), color: 'bg-orange-500', value: 'high' },
        { level: t('priority.medium'), description: t('priorityLevels.medium'), color: 'bg-blue-500', value: 'medium' },
        { level: t('priority.low'), description: t('priorityLevels.low'), color: 'bg-gray-500', value: 'low' }
    ];

    return (
        <main className="flex-1 p-6">
            <div className="max-w-6xl mx-auto">
                {!createdIncident && (
                    <>
                        <div className="mb-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('submitIncident.title')}</h1>
                            <p className="text-gray-600">{t('submitIncident.subtitle')}</p>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label
                                        htmlFor="description"
                                        className="block text-sm font-medium text-gray-700 mb-2"
                                    >
                                        {t('submitIncident.descriptionLabel')}
                                    </label>
                                    <div className="relative">
                                        <textarea
                                            id="description"
                                            value={incidentDescription}
                                            onChange={(e) => onDescriptionChange(e.target.value)}
                                            rows={6}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                            placeholder={t('submitIncident.descriptionPlaceholder')}
                                            required
                                        />
                                        <div className="absolute bottom-2 right-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const tips = t('submitIncident.contextTips');
                                                    onDescriptionChange(incidentDescription + tips);
                                                }}
                                                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center bg-blue-50 px-2 py-1 rounded border border-blue-100"
                                            >
                                                <FileText className="w-3 h-3 mr-1" />
                                                {t('submitIncident.moreContext')}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {onPriorityChange && (
                                    <div>
                                        <label
                                            htmlFor="priority"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            {t('submitIncident.priorityLabel')}
                                        </label>
                                        <select
                                            id="priority"
                                            value={priority}
                                            onChange={(e) => onPriorityChange(e.target.value as 'low' | 'medium' | 'high' | 'critical')}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="low">{t('priority.low')}</option>
                                            <option value="medium">{t('priority.medium')}</option>
                                            <option value="high">{t('priority.high')}</option>
                                            <option value="critical">{t('priority.critical')}</option>
                                        </select>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isSubmitting || !incidentDescription.trim()}
                                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                            {t('submitIncident.analyzingButton')}
                                        </span>
                                    ) : (
                                        t('submitIncident.submitButton')
                                    )}
                                </button>
                            </form>

                            {submitError && (
                                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-center">
                                        <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                                        <h3 className="text-red-800 font-medium">{t('common.error')}</h3>
                                    </div>
                                    <p className="mt-2 text-red-700">{submitError}</p>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Quick Tips Card */}
                            <InfoCard
                                title={t('submitIncident.tipsTitle')}
                                icon={AlertCircle}
                                iconColor="text-indigo-600"
                                iconBgColor="bg-indigo-100"
                            >
                                <ul className="space-y-2">
                                    {quickTips.map((tip, index) => (
                                        <li key={index} className="flex items-start">
                                            <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                            <span className="text-sm text-gray-600">{tip}</span>
                                        </li>
                                    ))}
                                </ul>
                            </InfoCard>

                            {/* Priority Levels Card */}
                            <InfoCard
                                title={t('submitIncident.priorityTitle')}
                                icon={TrendingUp}
                                iconColor="text-green-600"
                                iconBgColor="bg-green-100"
                            >
                                <ul className="space-y-3">
                                    {priorityLevels.map((priorityLevel, index) => (
                                        <li key={index} className="flex items-center">
                                            <div className={`w-3 h-3 rounded-full mr-3 ${priorityLevel.color}`}></div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-900">{priorityLevel.level}</span>
                                                <span className="text-sm text-gray-600"> - {priorityLevel.description}</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </InfoCard>

                            {/* Need Help Card */}
                            <InfoCard
                                title={t('submitIncident.needHelpTitle')}
                                icon={HelpCircle}
                                iconColor="text-purple-600"
                                iconBgColor="bg-purple-100"
                            >
                                <p className="text-sm text-gray-600 mb-4">
                                    For urgent issues, contact our support team directly.
                                </p>
                                <button className="flex items-center text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors">
                                    {t('submitIncident.contactSupport')}
                                    <ArrowRight className="w-4 h-4 ml-1" />
                                </button>
                            </InfoCard>
                        </div>
                    </>
                )}

                {createdIncident && (
                    <div className="space-y-8">
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <div className="flex items-center justify-between mb-6">
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {t('submitIncident.successTitle')}
                                </h1>
                                <span className="px-4 py-2 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                                    {t(`status.${createdIncident.status}`)?.toUpperCase()}
                                </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <span className="text-sm font-medium text-gray-500">{t('common.priority')}</span>
                                    <div className="mt-1">
                                        <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                                            createdIncident.priority === 'critical'
                                                ? 'bg-red-100 text-red-800'
                                                : createdIncident.priority === 'high'
                                                    ? 'bg-orange-100 text-orange-800'
                                                    : createdIncident.priority === 'medium'
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-green-100 text-green-800'
                                        }`}>
                                            {t(`priority.${createdIncident.priority}`).toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-500">{t('common.created')}</span>
                                    <p className="text-gray-900">
                                        {new Date(createdIncident.created_at).toLocaleString()}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-500">{t('common.user')}</span>
                                    <p className="text-gray-900">{createdIncident.user_id}</p>
                                </div>
                            </div>

                            <div className="mt-6">
                                <span className="text-sm font-medium text-gray-500">{t('common.title')}</span>
                                <p className="text-gray-900 text-lg font-medium mt-1">{createdIncident.title}</p>
                            </div>

                            <div className="mt-6">
                                <span className="text-sm font-medium text-gray-500">{t('incidentDetail.description')}</span>
                                <p className="text-gray-900 mt-1 bg-gray-50 p-4 rounded-lg">{createdIncident.description}</p>
                            </div>
                        </div>

                        {analysisLoading && !createdIncident.analysis && (
                            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                                <div className="flex flex-col items-center justify-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                                    <h3 className="text-lg font-medium text-gray-900">{t('submitIncident.analysisGenerating')}</h3>
                                    <p className="text-gray-500 mt-2">{t('submitIncident.analysisGeneratingMsg')}</p>
                                </div>
                            </div>
                        )}

                        {analysisError && (
                            <div className="bg-white rounded-lg shadow-lg p-8 text-center border border-red-200">
                                <div className="flex flex-col items-center justify-center">
                                    <AlertCircle className="h-10 w-10 text-red-600 mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900">{t('submitIncident.analysisFailed')}</h3>
                                    <p className="text-red-600 mt-2">{analysisError}</p>
                                </div>
                            </div>
                        )}

                        {createdIncident.analysis && (
                            <IncidentAnalysisComponent
                                analysis={createdIncident.analysis}
                                incidentId={createdIncident.id}
                            />
                        )}

                        <div className="flex justify-center space-x-4">
                            {onCreateAnother && (
                                <button
                                    onClick={onCreateAnother}
                                    className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium transition-colors"
                                >
                                    <RefreshCw className="w-5 h-5 mr-2" />
                                    {t('submitIncident.createAnother')}
                                </button>
                            )}
                            
                            {onEditIncident && (
                                <button
                                    onClick={onEditIncident}
                                    className="flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 font-medium transition-colors"
                                >
                                    <Edit2 className="w-5 h-5 mr-2" />
                                    {t('submitIncident.moreContext')}
                                </button>
                            )}

                            <button
                                onClick={onViewAllIncidents}
                                className="flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 font-medium transition-colors"
                            >
                                <List className="w-5 h-5 mr-2" />
                                {t('submitIncident.viewAll')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
}
